import {
  type GetTicketResponse,
  type GetUsersResponse,
  type BaseTicketComment,
  type BaseTicketMessage,
  type CreateTicketCommentResponse,
} from "@mg/schemas/src/christo/catalyst";
import {
  EnterpriseProfileType,
  KnowledgeDataRecordProperty,
  TicketCommentDisposition,
  TicketMessage,
} from "@mg/schemas/src/commons";
import { type ShowcaseToken } from "@mg/schemas/src/prince/auth";
import {
  ArrowSquareOut,
  CheckCircle,
  PencilSimple,
  PencilSimpleSlash,
  ThumbsDown,
  ThumbsUp,
  Trash,
  Warning,
  CaretDown,
  CaretLeft,
  XCircle,
} from "@phosphor-icons/react";
import {
  Button,
  Checkbox,
  Dialog,
  Flex,
  Grid,
  IconButton,
  Select,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { type Editor } from "@tldraw/tldraw";
import cx from "classnames";
import { formatDistanceToNowStrict } from "date-fns";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import { twMerge } from "tailwind-merge";

import { AvatarWithInitials } from "../../../../../components/AvatarWithInitials";
import { Preview } from "../../../../../components/Preview";
import leapfrogLoader from "../../../../../images/leapfrog-gray.gif";
import { useAnalytics } from "../../../../../utils/analytics";
import {
  canDeleteComments,
  canDismissComments,
  canDoRestrictedCommentEdit,
  canResolveComments,
  isPunttGuest,
  personaName,
} from "../../../../../utils/auth";
import { renderPersonaAvatar } from "../../../../../utils/helpers/ai-persona-avatars";
import { useAppDispatch, useAppSelector } from "../../../../../utils/hooks";
import { useGetKnowledgeDataRecord } from "../../../../../utils/queries/knowledge-db";
import {
  useCreateAskPunttComment,
  useDeleteCommentMutation,
  useEditCommentMutation,
  useNewCommentMutation,
} from "../../../../../utils/queries/projects";
import { getAiRuleMap, atomicTaskMap } from "../../../../../utils/selections";
import {
  setComments,
  setHighlightedCommentId,
} from "../../../../../utils/slices/ticket";
import { knowledgeDatabaseRoute } from "../../../../settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../../../../settings/routes/knowledgeSettingRoute";
import {
  CommentMentions,
  type Selections,
} from "../../../components/CommentMentions/CommentMentions";
import { MessageComponent } from "../../../components/CommentTool/MessageComponent";
import { formatTimeStamp } from "../../../components/VideoPlayback";

const commentActionClasses = "transition-opacity text-puntt-neutral-gray-11";

// Context for per-thread and global collapse/expand
export const RepliesCollapseContext = createContext<{
  collapsed: Record<string, boolean>;
  setCollapsed: (threadId: string, value: boolean) => void;
  setAll: (value: boolean) => void;
} | null>(null);

type CommentProps = {
  /**
   * The author of the comment
   */
  author?: {
    _id?: string | undefined;
    avatar?: string | undefined;
    name?: string | undefined;
  };
  /**
   * The persona of the AI leaving the comment
   */
  aiPersona?: {
    name: string;
    avatar: "brand" | "legal" | "copy" | string;
  };
  /**
   * The timestamp (string or date) or the comment
   * submission. This value is rendered relative to the
   * current time (e.g. "15 minutes ago").
   */
  createdAt: string;
  /**
   * Optionally, whether the comment is an AI comment or
   * not; defaults to `false`.
   */
  isAI?: boolean;
  /**
   * Optionally, if the comment is a reply to another comment
   */
  isReply?: boolean;
  /**
   * The message text of the comment.
   */
  message: string;
  /**
   * The ID of the comment or message, used for editing and
   * deleting a message
   */
  messageId: string;
  /**
   * The original comment object
   */
  meta: BaseTicketComment;
  /**
   * Handler when any comment or reply is clicked
   */
  onClick?(): void;
  /**
   * An array of replies to the main comment
   */
  replies?: BaseTicketMessage[];
  /**
   * Optionally, forces the reply input to render. This is
   * because we conditionally render the reply box on the
   * root comment, or on the last reply if one is available
   */
  showReplyInput?: boolean;
  /**
   * An optional title that appears at the top of the
   * component (e.g. which revision you are actively
   * reviewing)
   */
  title?: string;
  updatedAt: string;
  onSuccess?: (res: CreateTicketCommentResponse, eventName: string) => void;
  onError?: (
    error: Error | Response,
    data: CreateTicketCommentResponse,
    operation: string,
    payload: Record<string, string | number | boolean | string[] | undefined>,
  ) => void;
  mentions?: string[];
  rule?: string;
  editor: Editor | null | undefined;
  users?: GetUsersResponse;
  /**
   * Used to show a background color for active comments
   * when the comment is clicked
   * */
  isActive?: boolean;
  videoStart?: number;
  commentId: string;
  isAIPending?: boolean;
  isNotFirstReply?: boolean;
  isCanvas?: boolean;
  isLast?: boolean;
  citations?: TicketMessage["citations"];
  setMessage?: (message: string) => void;
  tempComment?: string;
};

function analyticsPayload(
  renderTime: number,
  comment: BaseTicketComment,
  messageId: string,
  ticket: GetTicketResponse,
  comments: BaseTicketComment[],
  revisionName?: string,
  user?: ShowcaseToken | null,
) {
  const message =
    comment._id === messageId
      ? comment
      : comment.messages.find((m) => m._id === messageId);
  const currentRevision = ticket.revisionBoards?.find(
    (r) => r.name === revisionName,
  );
  const isUselessMessage =
    message?.description === "Sorry, I can't help with that.";

  return {
    rootCommentCreatedAt: comment.createdAt,
    rootCommentUpdatedAt: comment.updatedAt,
    rootCommentIsAi: comment.isAI,
    rootCommentText: comment.description,
    rootCommentId: comment._id,
    rootCommentIsRequired: comment.isRequired,
    rootCommentRule: comment.rule,
    rootCommentHasAiPersona: Boolean(comment.aiPersona),
    messageCreatedAt: message?.createdAt,
    messageUpdatedAt: message?.updatedAt,
    messageIsAi: message?.isAI,
    messageText: message?.description,
    messageId: message?._id,
    invokesPuntt: message?.description.includes("@puntt"),
    isUselessMessage,
    commentIsReply: comment._id !== messageId,
    numReplies: comment.messages.length,
    numComments: comments.length,
    numNewComments: comments.filter(
      (c) => new Date(c.createdAt).getTime() > renderTime,
    ).length,
    numActiveCommentsOnCurrentRevision: comments.filter(
      (c) =>
        c.boardId === currentRevision?._id &&
        c.disposition === TicketCommentDisposition.DEFAULT,
    ).length,
    numRevisions: ticket.revisionBoards?.length,
    revisionName,
    revisionId: currentRevision?._id,
    revisionNumber: currentRevision?.revision,
    revisionFileTypes: currentRevision?.reviewFiles?.map((f) =>
      f.source.slice(-3),
    ),
    minutesSinceRevisionCreated:
      (Date.now() -
        new Date(currentRevision?.createdAt ?? Date.now()).getTime()) /
      (1000 * 60),
    revisionCreatedByCurrentUser:
      (typeof currentRevision?.createdBy === "string"
        ? currentRevision?.createdBy
        : currentRevision?.createdBy._id) === user?.userID &&
      Boolean(user?.userID),
    numMentions: message?.mentions?.length ?? 0,
    messageMentions: message?.mentions?.map((m) =>
      typeof m === "string" ? m : m.email,
    ),
    minutesSinceRender: (Date.now() - renderTime) / (1000 * 60),
    x: comment.x,
    y: comment.y,
    boardType: comment.boardType,
    boardId: comment.boardId,
    disposition: comment.disposition,
  };
}

export function Comment(props: CommentProps) {
  const {
    author,
    createdAt,
    isAI = false,
    isReply = false,
    message,
    messageId,
    meta,
    onClick,
    replies = [],
    showReplyInput = false,
    title,
    updatedAt,
    onSuccess,
    onError,
    mentions,
    rule,
    editor,
    users,
    isActive,
    videoStart,
    commentId,
    isAIPending,
    isNotFirstReply,
    isCanvas,
    isLast: _isLast,
    citations,
    setMessage,
    tempComment,
    aiPersona,
  } = props;

  const itemRef = useRef<HTMLDivElement>(null);
  const posthog = useAnalytics("Comment");
  const user = useAppSelector((state) => state.auth.value);
  const userIsMG = user?.role === EnterpriseProfileType.MEANINGFUL_GIGS;
  const userIsAI = user?.role === EnterpriseProfileType.CATALYST_AI;
  const userIsAdmin = user?.role === EnterpriseProfileType.ADMIN;
  // Special AI accounts that don't have traditional roles but follow ai+enterprise@puntt.ai pattern
  const isSpecialAIAccount =
    user?.email?.startsWith("ai+") && user?.email?.endsWith("@puntt.ai");
  const isGuest = isPunttGuest();
  const { comments, value: ticket } = useAppSelector((state) => state.ticket);
  const dispatch = useAppDispatch();
  const createCommentMutation = useNewCommentMutation();
  const editCommentMutation = useEditCommentMutation();
  const deleteCommentMutation = useDeleteCommentMutation({
    parentCommentId: meta._id,
    editor,
  });
  const askPunttMutation = useCreateAskPunttComment();

  const manageBrandGuidelines = useFeatureFlagVariantKey(
    "manage-brand-guidelines",
  );

  const canEditCommentRequired = canDoRestrictedCommentEdit();

  // used to edit the comment's text
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message);
  // used to reply to the comment
  const [replyText, setReplyText] = useState(tempComment ?? "");
  // used to toggle the thumbs icon states. This state does
  // not persist refreshes.
  const [liked, setLiked] = useState(meta.liked);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequired, setIsRequired] = useState<boolean | "indeterminate">(
    meta.isRequired,
  );
  const [editedRule, setEditedRule] = useState(rule ?? "no_rule");
  const [editedAtomicTask, setEditedAtomicTask] = useState(
    meta?.atomic_task || "no_atomic_task",
  );
  const aiRuleMap = getAiRuleMap();

  const mentionsRef = useRef<{
    getSelections: () => Selections;
    closeMentions: () => void;
  }>();

  const editMentionsRef = useRef<{
    getSelections: () => Selections;
    closeMentions: () => void;
  }>();

  const renderTimeRef = useRef(0);
  useEffect(() => {
    renderTimeRef.current = Date.now();
  }, [ticket?._id]);

  const currentCitations = citations || isReply ? citations : meta.citations;
  const moreTextCitations = currentCitations
    ?.slice(1)
    .filter(
      (citation): citation is { text: string } =>
        "text" in citation && Boolean(citation.text),
    );
  // Being a little less strict with the union of citation types makes this much easier to work with
  const citation =
    (currentCitations?.[0] as {
      url?: string;
      pageNumber?: number;
      text?: string;
      knowledgeDataRecordId?: string;
    }) || false;

  const getKnowledgeDataRecord = useGetKnowledgeDataRecord(
    citation ? citation.knowledgeDataRecordId : undefined,
  );
  const [knowledgeDataRecord, setKnowledgeDataRecord] =
    useState<KnowledgeDataRecordProperty[]>();
  useEffect(() => {
    if (!citation || !citation.knowledgeDataRecordId) return;
    const data = getKnowledgeDataRecord.data;
    if (!data) return;
    setKnowledgeDataRecord(data.data);
  }, [citation, getKnowledgeDataRecord.data]);

  const handleGetSelections = () => {
    if (mentionsRef.current) {
      return mentionsRef.current.getSelections();
    }
  };

  const handleEditGetSelections = () => {
    if (editMentionsRef.current) {
      return editMentionsRef.current.getSelections();
    }
  };

  const handleSetReplyText = (text: string) => {
    setReplyText(text);
    if (setMessage) {
      setMessage(text);
    }
  };

  const isEdited = createdAt !== updatedAt;

  // Special logic for AI accounts: if user is an AI account (ai+enterprise@puntt.ai pattern)
  // and the comment is an AI comment, treat it as their own comment
  const isOwnComment =
    user?.userID === author?._id || (isSpecialAIAccount && isAI);
  useEffect(() => {
    setEditText(message);
  }, [message]);

  useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isActive, meta.isRequired]);

  function handleThumbsUp() {
    const startTime = performance.now();

    if (!ticket || editCommentMutation.isPending) return;
    const newLiked = liked === true ? null : true;
    setLiked(newLiked);

    const payload: any = {
      description: message,
      liked: newLiked,
    };

    editCommentMutation.mutateAsync({
      ticketId: ticket._id,
      messageId,
      payload,
    });

    posthog.capture("thumbs_up_comment", {
      ...analyticsPayload(
        renderTimeRef.current,
        meta,
        messageId,
        ticket,
        comments,
        title,
        user,
      ),
      requestDurationSeconds: (performance.now() - startTime) / 1000,
    });
  }

  // Everyone should be able to leave a thumbs up on an AI
  // comment regardless of user account type.
  function renderThumbsUpAction() {
    if (!isAI || messageId !== commentId) {
      return null;
    }

    return (
      <Tooltip content={liked === true ? "Remove thumbs up" : "Thumbs up"}>
        <IconButton
          data-testid="thumbs-up-action"
          size="1"
          variant="soft"
          onClick={handleThumbsUp}
          color={liked === true ? "green" : undefined}
          className={commentActionClasses}
          data-auth-trigger="thumbs-up"
        >
          <ThumbsUp
            weight={liked === true ? "fill" : "regular"}
            height="16"
            width="16"
            className="text-puntt-neutral-gray-11"
          />
        </IconButton>
      </Tooltip>
    );
  }

  function handleThumbsDown() {
    const startTime = performance.now();

    if (!ticket || editCommentMutation.isPending) return;
    const newLiked = liked === false ? undefined : false;
    setLiked(newLiked);

    const payload: any = {
      description: message,
    };
    if (newLiked !== undefined) {
      payload.liked = newLiked;
    }

    editCommentMutation.mutateAsync({
      ticketId: ticket._id,
      messageId,
      payload,
    });

    posthog.capture("thumbs_down_comment", {
      ...analyticsPayload(
        renderTimeRef.current,
        meta,
        messageId,
        ticket,
        comments,
        title,
        user,
      ),
      requestDurationSeconds: (performance.now() - startTime) / 1000,
    });
  }

  // Only MG and AI Studio users can thumbs down.
  function renderThumbsDownAction() {
    if (!isAI || messageId !== commentId) {
      return null;
    }

    return (
      <Tooltip content={liked === false ? "Remove thumbs down" : "Thumbs down"}>
        <IconButton
          data-testid="thumbs-down-action"
          size="1"
          variant="soft"
          onClick={handleThumbsDown}
          color={liked === false ? "red" : undefined}
          className={commentActionClasses}
          data-auth-trigger="thumbs-down"
        >
          <ThumbsDown
            weight={liked === false ? "fill" : "regular"}
            height="16"
            width="16"
            className="text-puntt-neutral-gray-11"
          />
        </IconButton>
      </Tooltip>
    );
  }

  // Track if we've just submitted our first reply
  const [justSubmittedFirstReply, setJustSubmittedFirstReply] = useState(false);

  // Add an effect for handling first reply cleanup
  useEffect(() => {
    if (justSubmittedFirstReply) {
      // Reset the reply text and the flag
      setReplyText("");
      if (setMessage) {
        setMessage("");
      }
      setJustSubmittedFirstReply(false);
    }
  }, [justSubmittedFirstReply, setMessage]);

  // Reset replyText when tempComment changes externally
  useEffect(() => {
    if (tempComment !== undefined) {
      setReplyText(tempComment);
    }
  }, [tempComment]);

  function handleReply() {
    if (ticket == null || replyText.trim() === "" || isLoading) {
      return;
    }

    const isVeryFirstReply = replies.length === 0;
    setIsLoading(true);
    const selections = handleGetSelections();

    if (selections?.find((s) => s._id === "@puntt")) {
      setIsLoading(false);

      const commentsCopy = comments.map((comm) => {
        if (comm._id === meta._id) {
          return {
            ...comm,
            messages: [
              ...comm.messages,
              {
                _id: (Math.random() * 10000).toString(),
                createdBy: {
                  _id: user?.userID,
                  name: user?.name ?? "Anonymous",
                  avatar: user?.avatar,
                },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isDeleted: false,
                isPending: false,
                isReview: false,
                votes: [],
                isAI: false,
                description: replyText,
                mentions: [],
              },
              {
                createdBy: {
                  _id: "63bc994226c323d41d67a11f",
                  name: "AI Reviewer",
                  avatar:
                    "users/615f5203e1cb445ef2486b74/3-color-logo-white-bg-square.png",
                },
                aiPersona: {
                  name: "",
                  avatar: "",
                },
                description: "",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isDeleted: false,
                isPending: true,
                isReview: false,
                votes: [],
                isAI: true,
                reviewId: "67082ad6ecce06a419fe876d",
                mentions: [],
                messages: [],
                x: 20,
                y: 20,
                boardType: "revisionBoard",
                boardId: "6706ca0b6a3ca36a292610a5",
                disposition: "default",
                _id: (Math.random() * 100000).toString(),
                aiPending: true,
              },
            ],
          };
        }

        return comm;
      });
      const updatedComment = commentsCopy.find((comm) => comm._id === meta._id);
      dispatch(setComments(commentsCopy));
      if (onSuccess) onSuccess(updatedComment!, "reply to @puntt");

      const payload = {
        ticketId: ticket._id,
        description: replyText,
        x: meta.x as number,
        y: meta.y as number,
        boardId: meta.boardId as string,
        commentId: meta._id,
        shapeIds: [],
        isRequired: meta.isRequired,
      };
      return askPunttMutation
        .mutateAsync(payload)
        .then((res) => {
          if (onSuccess) onSuccess(res, "reply from @puntt");
        })
        .catch((error: unknown) => {
          dispatch(setComments(comments));
          if (onError)
            onError(
              error as Error | Response,
              meta,
              "asking Puntt AI",
              payload,
            );
        })
        .finally(() => {
          setReplyText("");
          if (setMessage) {
            setMessage("");
          }
          setIsLoading(false);
        });
    }

    const payload = {
      description: replyText,
      x: meta.x,
      y: meta.y,
      boardId: meta.boardId,
      commentId: meta._id, // always the parent comment ID
      mentions: selections
        ?.map((s) => s._id)
        .filter((id): id is string => id !== undefined),
    };

    // Mark that we've submitted first reply if this is the first one
    if (isVeryFirstReply) {
      setJustSubmittedFirstReply(true);
    }

    createCommentMutation
      .mutateAsync({
        ticketId: ticket._id,
        payload,
      })
      .then((res) => {
        if (onSuccess) onSuccess(res, "reply");
        setReplyText("");
        if (setMessage) {
          setMessage("");
        }
      })
      .catch((error: unknown) => {
        if (onError)
          onError(error as Error | Response, meta, "creating comment", payload);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const [isEditingPending, setIsEditingPending] = useState(false);
  function handleEdit() {
    if (ticket == null || editText.trim().length === 0) {
      return;
    }

    const selections = handleEditGetSelections();

    const uniqueMentions =
      Array.from(
        new Set(
          selections
            ?.map((s) => s._id)
            .filter((id): id is string => id !== undefined),
        ),
      ) ?? [];

    const hasPunttMention = selections?.some((s) => s._id === "@puntt");

    if (hasPunttMention) {
      setIsEditingPending(true);
      setIsLoading(true);
      const commentsCopy = comments.map((comm) => {
        if (comm._id === meta._id) {
          const messages = comm.messages;

          // Find the index of the message being edited
          const messageIndex = messages.findIndex(
            (msg) => msg._id === messageId,
          );
          const isRootComment = comm._id === messageId;

          const aiResponse = {
            aiPersona: {
              name: "",
              avatar: "",
            },
            description: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isDeleted: false,
            isPending: true,
            isReview: false,
            votes: [],
            isAI: true,
            reviewId: "67082ad6ecce06a419fe876d",
            mentions: [],
            messages: [],
            x: 20,
            y: 20,
            boardType: "revisionBoard",
            boardId: "6706ca0b6a3ca36a292610a5",
            disposition: "default",
            _id: (Math.random() * 100000).toString(),
            aiPending: true,
          };
          if (isRootComment) {
            // If editing the root comment, update its description and add AI response as first message
            return {
              ...comm,
              description: editText,
              messages: [aiResponse, ...messages],
            };
          } else {
            // If editing a reply, update the message and insert AI response after it
            const updatedMessages = [...messages];
            updatedMessages[messageIndex] = {
              ...messages[messageIndex],
              description: editText,
            };
            updatedMessages.splice(messageIndex + 1, 0, aiResponse);
            return {
              ...comm,
              messages: updatedMessages,
            };
          }
        }
        return comm;
      });

      const updatedComment = commentsCopy.find((comm) => comm._id === meta._id);
      dispatch(setComments(commentsCopy));
      if (onSuccess) onSuccess(updatedComment!, "edit reply to @puntt");
      setIsEditing(false);
      editMentionsRef.current?.closeMentions();
      // Then create the AI response

      const punttPayload = {
        ticketId: ticket._id,
        commentId: meta._id,
        description: editText,
        x: typeof meta.x === "number" ? meta.x : 0,
        y: typeof meta.y === "number" ? meta.y : 0,
        boardId: meta.boardId || "",
        shapeIds: [] as string[],
        disposition: meta.disposition || "default",
        liked: meta.liked,
        rule: isReply ? undefined : editedRule,
        atomic_task: isReply
          ? undefined
          : editedAtomicTask === "no_atomic_task"
            ? ""
            : editedAtomicTask,
        isRequired: isRequired === true,
        isEdit: true,
      };
      if (messageId != commentId) {
        // @ts-expect-error TS2399: its there optionally
        punttPayload.messageId = messageId;
      }

      const startTime = performance.now();
      askPunttMutation
        .mutateAsync(punttPayload)
        .then((res) => {
          posthog.capture("edit_comment", {
            ...analyticsPayload(
              renderTimeRef.current,
              meta,
              messageId,
              ticket,
              comments,
              title,
              user,
            ),
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            isEdit: true,
          });

          if (onSuccess) onSuccess(res, "@puntt reply to edit");
        })
        .catch((error) => {
          dispatch(setComments(comments));
          if (onError) {
            // @ts-expect-error TS2399: its there optionally
            onError(error, meta, "editing to ask Puntt AI", punttPayload);
          }
          // Add a posthog event to track the error
          posthog.capture("ask_puntt_edit_comment_error", {
            ...analyticsPayload(
              renderTimeRef.current,
              meta,
              messageId,
              ticket,
              comments,
              title,
              user,
            ),
          });
        })
        .finally(() => {
          setIsEditingPending(false);
          setIsLoading(false);
          editMentionsRef.current?.closeMentions();
        });
    } else {
      const startTime = performance.now();
      // Regular edit without AI
      const payload = {
        description: editText,
        mentions: uniqueMentions,
        liked: meta.liked,
        isRequired: isRequired === true,
        rule: isReply ? undefined : editedRule,
        atomic_task: isReply
          ? undefined
          : editedAtomicTask === "no_atomic_task"
            ? ""
            : editedAtomicTask,
      };
      editCommentMutation
        .mutateAsync({
          ticketId: ticket._id,
          messageId,
          payload,
        })
        .then((res) => {
          posthog.capture("edit_comment", {
            ...analyticsPayload(
              renderTimeRef.current,
              meta,
              messageId,
              ticket,
              comments,
              title,
              user,
            ),
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            askedPuntt: false,
          });
          setIsEditing(false);
          editMentionsRef.current?.closeMentions();
          if (onSuccess) onSuccess(res, "edit reply");
        })
        .catch((error) => {
          // @ts-expect-error TS2399: its there optionally
          if (onError) onError(error, meta, "editing comment", payload);
          posthog.capture("edit_comment_error", {
            ...analyticsPayload(
              renderTimeRef.current,
              meta,
              messageId,
              ticket,
              comments,
              title,
              user,
            ),
          });
        });
    }
  }

  function handleDelete() {
    if (ticket == null) {
      return;
    }

    const startTime = performance.now();
    deleteCommentMutation.mutate({
      ticketId: ticket._id,
      messageId,
    });

    posthog.capture("deleted_ticket_comment", {
      ...analyticsPayload(
        renderTimeRef.current,
        meta,
        messageId,
        ticket,
        comments,
        title,
        user,
      ),
      requestDurationSeconds: (performance.now() - startTime) / 1000,
    });
  }

  const aiPersonaName =
    isAI && aiPersona?.avatar ? aiPersona?.avatar : (rule ?? "");
  function renderAvatar() {
    if (isAI) {
      return renderPersonaAvatar(aiPersonaName);
    }

    return (
      <AvatarWithInitials
        data-testid="user-avatar"
        name={author?.name}
        avatar={author?.avatar}
        size={6}
      />
    );
  }

  function renderEditAction() {
    function canEditComment() {
      // Everyone should be able to edit their own comments
      if (isOwnComment) {
        return true;
      }
      // MG users and AI agents can edit AI comments
      if (isAI && (userIsMG || userIsAI)) {
        return true;
      }

      if (canEditCommentRequired && isAI) {
        return true;
      }

      return false;
    }

    const mentionRegex = new RegExp(
      `(^|\\W)@(${["puntt"].join("|")})\\b`,
      "gi",
    );
    const hasPunttMention = mentionRegex.test(message);
    const isDisabled = isEditingPending || hasPunttMention;

    const button = (
      <Tooltip content={"Edit"}>
        <IconButton
          data-testid="edit-action"
          size="1"
          variant="soft"
          onClick={() => {
            if (!isDisabled) {
              setIsEditing(!isEditing);
            }
          }}
          className={commentActionClasses}
          data-auth-trigger="edit"
          disabled={isDisabled}
        >
          {isEditing ? (
            <PencilSimpleSlash height="16" width="16" />
          ) : (
            <PencilSimple height="16" width="16" />
          )}
        </IconButton>
      </Tooltip>
    );

    if (!canEditComment()) {
      return null;
    }

    if (isDisabled) {
      return (
        <Tooltip
          content={
            hasPunttMention
              ? `@Puntt questions \ncannot be edited`
              : `Cannot edit while saving is taking place`
          }
          side="left"
          maxWidth="110px"
          align="center"
        >
          {button}
        </Tooltip>
      );
    }

    return button;
  }

  function renderDeleteAction() {
    function canDeleteComment() {
      // if the current user left the comment
      if (isOwnComment) {
        return true;
      }

      // MG and Admin users can delete (but not edit) any non-AI comment
      if (canDeleteComments()) {
        return true;
      }

      // MG users and AI agents can delete AI comments
      if (isAI && (userIsMG || userIsAI || userIsAdmin)) {
        return true;
      }

      return false;
    }

    if (!canDeleteComment() || isEditing || isEditingPending) {
      return null;
    }

    return (
      <Tooltip content="Delete">
        <IconButton
          data-testid="delete-action"
          size="1"
          variant="soft"
          onClick={(e) => {
            // if the shape that they are deleting is not currently selected,
            // we should prevent it from being selected flickering and then immediately being deleted
            e.stopPropagation();
            handleDelete();
          }}
          disabled={deleteCommentMutation.isPending}
          loading={deleteCommentMutation.isPending}
          className={`${commentActionClasses} text-puntt-red-9 transition-colors hover:bg-puntt-red-9 hover:text-base-white`}
          data-auth-trigger="delete"
        >
          <Trash height="16" width="16" />
        </IconButton>
      </Tooltip>
    );
  }

  function renderCommentContents() {
    // if its not their comment, and their role is not MG or AI, they cannot edit the comment text, but they can edit the rule and required
    function canEditComment() {
      if (isOwnComment) {
        return true;
      }
      if (isAI && (userIsMG || userIsAI || userIsAdmin)) {
        return true;
      }
      return false;
    }
    if (isEditing) {
      return (
        <Grid gap="1">
          {!canEditComment() ? (
            <MessageComponent
              mentions={mentions}
              messageContent={message}
              users={users}
              rule={rule}
            />
          ) : (
            <CommentMentions
              ref={editMentionsRef}
              data-testid="edit-input"
              placeholder="Reply or Ask @Puntt"
              onChange={setEditText}
              value={editText}
              disabled={editCommentMutation.isPending}
              loading={editCommentMutation.isPending}
              onSend={handleEdit}
              isEdit
              data-auth-trigger="edit"
              key={messageId}
            />
          )}
          <Flex gap="2" align="center" justify="between" mt="2">
            <Text
              size="1"
              as="label"
              className={cx("text-base-black", {
                hidden:
                  user?.role === EnterpriseProfileType.CATALYST_REQUESTER ||
                  user?.role === EnterpriseProfileType.CATALYST_CREATIVE ||
                  messageId != commentId,
              })}
            >
              <Flex gap="2" className="items-center">
                <Checkbox
                  size="1"
                  defaultChecked={meta.isRequired}
                  onCheckedChange={setIsRequired}
                />
                Required Change
              </Flex>
            </Text>
            <Button
              size="1"
              data-testid="edit-comment-save-action"
              disabled={
                editText.trim().length === 0 || editCommentMutation.isPending
              }
              loading={editCommentMutation.isPending}
              onClick={() => handleEdit()}
              className="float-right mt-2 min-h-6 w-1/2"
            >
              Save change
            </Button>
          </Flex>
          <Select.Root
            size="1"
            value={editedRule}
            onValueChange={setEditedRule}
          >
            <Select.Trigger
              className={cx("w-full", {
                hidden:
                  (user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS &&
                    user?.role !== EnterpriseProfileType.CATALYST_AI) ||
                  !isRequired ||
                  messageId != commentId,
              })}
              variant="surface"
              color="gray"
            />
            <Select.Content>
              <Select.Item value="no_rule">No Rule</Select.Item>
              {Object.keys(aiRuleMap).map((key) => (
                <Select.Item key={key} value={key}>
                  {aiRuleMap[key]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Select.Root
            size="1"
            value={editedAtomicTask}
            onValueChange={setEditedAtomicTask}
          >
            <Select.Trigger
              className={cx("w-full", {
                hidden:
                  (user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS &&
                    user?.role !== EnterpriseProfileType.CATALYST_AI) ||
                  messageId !== commentId,
              })}
              variant="surface"
              color="gray"
            />
            <Select.Content>
              <Select.Item value="no_atomic_task">No Atomic Task</Select.Item>
              {Object.entries(atomicTaskMap).map(([key, label]) => (
                <Select.Item key={key} value={key}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Grid>
      );
    }

    return (
      <MessageComponent
        mentions={mentions}
        messageContent={message}
        users={users}
        rule={rule}
      />
    );
  }

  function renderCitation() {
    if (!citation) return null;
    if (citation.knowledgeDataRecordId && !knowledgeDataRecord) {
      if (getKnowledgeDataRecord.isError) {
        let errorMessage = "Error loading source details";
        const error = getKnowledgeDataRecord.error;
        if (error instanceof Response && error.status === 404) {
          errorMessage = "Source was deleted from the Knowledge Database";
        } else if (error.message !== "Failed to fetch") {
          errorMessage += `: ${error.message}`;
        }

        return (
          <Tooltip content={errorMessage}>
            <Flex
              className="mt-2 w-max cursor-not-allowed text-puntt-accent-12 opacity-50"
              gap="1"
            >
              <Text className="underline" size="3">
                Source
              </Text>
              <Warning width={16} height={16} className="mt-1" />
            </Flex>
          </Tooltip>
        );
      }

      return (
        <Flex
          className="mt-2 w-max cursor-wait text-puntt-accent-12 opacity-50"
          gap="1"
        >
          <Text className="underline" size="3">
            Source
          </Text>
          <ArrowSquareOut width={16} height={16} className="mt-1" />
        </Flex>
      );
    }

    return (
      <Dialog.Root>
        <Dialog.Trigger>
          <Flex
            className="mt-2 w-max cursor-pointer text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
            gap="1"
          >
            <Text className="underline" size="3">
              Source
            </Text>
            <ArrowSquareOut width={16} height={16} className="mt-1" />
          </Flex>
        </Dialog.Trigger>
        <Dialog.Content
          className="flex flex-col gap-4"
          style={
            !citation.url && (citation.text || citation.knowledgeDataRecordId)
              ? {} // Smaller width for text-only citations
              : {
                  width: "90vw",
                  maxWidth: "80rem",
                  height: "90vh",
                  maxHeight: "60rem",
                }
          }
        >
          <Flex justify="between" align="center" gap="4">
            <Dialog.Title className="mb-0">Comment source details</Dialog.Title>
            <Dialog.Description className="hidden">
              Citation details
            </Dialog.Description>
            <Flex gap="2" align="center">
              <Button
                className={cx(
                  "hover:bg-puntt-accent-10 hover:text-base-white",
                  {
                    hidden:
                      (!citation.url &&
                        manageBrandGuidelines !== "enabled" &&
                        !knowledgeDataRecord) ||
                      isGuest,
                  },
                )}
                variant="outline"
                size="2"
                asChild
              >
                <Link
                  to={
                    knowledgeDataRecord
                      ? knowledgeDatabaseRoute.to
                      : citation.url
                        ? citation.url.endsWith(".pdf")
                          ? citation.url +
                            "?response-content-disposition=inline&response-content-type=application%2Fpdf"
                          : citation.url
                        : knowledgeSettingRoute.to
                  }
                  search={(): any => {
                    const modelId = getKnowledgeDataRecord.data?.modelId;
                    return modelId ? { tab: modelId } : {};
                  }}
                  target="_blank"
                  rel="noreferrer"
                  className="puntt-link flex gap-1"
                >
                  <Text>
                    {citation.url
                      ? "Open in New Tab"
                      : citation.knowledgeDataRecordId
                        ? "View Knowledge Database"
                        : "View Knowledge Settings"}
                  </Text>
                  <ArrowSquareOut width={16} height={16} />
                </Link>
              </Button>
              {(citation as any).originalURL && (
                <Button
                  className="hover:bg-puntt-accent-10 hover:text-base-white"
                  variant="outline"
                  size="2"
                  asChild
                >
                  <Link
                    to={(citation as any).originalURL}
                    target="_blank"
                    rel="noreferrer"
                    className="puntt-link flex gap-1"
                  >
                    <Text>Open Original URL</Text>
                    <ArrowSquareOut width={16} height={16} />
                  </Link>
                </Button>
              )}
            </Flex>
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                Close
              </Button>
            </Dialog.Close>
          </Flex>
          <Preview
            url={citation.url}
            pageNumber={citation.pageNumber}
            text={citation.text}
            textPrefix="From custom guidelines:"
            knowledgeDataRecord={knowledgeDataRecord}
          />
          {/* If the first two citations are both text, show the second one too */}
          {citation.text &&
            currentCitations?.[1] &&
            currentCitations?.[1] === (moreTextCitations ?? [])[0] && (
              <Preview text={moreTextCitations?.[0].text} />
            )}
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  function handleChangeDisposition(disposition: TicketCommentDisposition) {
    if (isReply || ticket == null) {
      return;
    }

    return editCommentMutation
      .mutateAsync({
        ticketId: ticket._id,
        messageId,
        payload: {
          description: message,
          disposition,
        },
      })
      .then((res) => {
        if (onSuccess) onSuccess(res, `disposition ${disposition}`);
      });
  }

  function renderDismissAction() {
    if (isReply || !canDismissComments() || isOwnComment) {
      return null;
    }

    return (
      <Tooltip content="Dismiss">
        <IconButton
          data-testid="dismiss-action"
          size="1"
          variant="soft"
          disabled={meta.disposition !== TicketCommentDisposition.DEFAULT}
          onClick={async () => {
            const startTime = performance.now();
            await handleChangeDisposition(TicketCommentDisposition.DISMISSED);

            if (!ticket) return;
            posthog.capture("dismissed_ticket_comment", {
              ...analyticsPayload(
                renderTimeRef.current,
                meta,
                messageId,
                ticket,
                comments,
                title,
                user,
              ),
              requestDurationSeconds: (performance.now() - startTime) / 1000,
            });
          }}
          className={twMerge(
            cx(commentActionClasses, {
              "opacity-100":
                meta.disposition === TicketCommentDisposition.RESOLVED,
            }),
          )}
          data-auth-trigger="dismiss"
        >
          <XCircle
            weight={
              meta.disposition === TicketCommentDisposition.DISMISSED
                ? "fill"
                : "regular"
            }
            height="16"
            width="16"
          />
        </IconButton>
      </Tooltip>
    );
  }

  // Add local hover state for the resolved button only
  const [isResolvedHovered, setIsResolvedHovered] = useState(false);

  function renderResolveOrUnresolveAction() {
    if (isReply || !canResolveComments()) {
      return null;
    }

    const isResolved = meta.disposition === TicketCommentDisposition.RESOLVED;
    const handleDispositionChange = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const startTime = performance.now();
      const newDisposition = isResolved
        ? TicketCommentDisposition.DEFAULT
        : TicketCommentDisposition.RESOLVED;

      await handleChangeDisposition(newDisposition);

      if (!ticket) return;

      posthog.capture(
        isResolved ? "unresolved_ticket_comment" : "resolved_ticket_comment",
        {
          ...analyticsPayload(
            renderTimeRef.current,
            meta,
            messageId,
            ticket,
            comments,
            title,
            user,
          ),
          requestDurationSeconds: (performance.now() - startTime) / 1000,
        },
      );
    };

    return (
      <Tooltip
        content={
          isResolved && isResolvedHovered
            ? "Unresolve Comment"
            : isResolved
              ? "Resolved"
              : "Resolve"
        }
      >
        <IconButton
          data-testid={isResolved ? "unresolve-action" : "resolve-action"}
          size="1"
          variant="soft"
          disabled={
            !isResolved && meta.disposition !== TicketCommentDisposition.DEFAULT
          }
          onClick={handleDispositionChange}
          className={twMerge(
            commentActionClasses,
            isResolved ? "!opacity-100" : "",
          )}
          data-auth-trigger={isResolved ? "unresolve" : "resolve"}
          onMouseEnter={
            isResolved ? () => setIsResolvedHovered(true) : undefined
          }
          onMouseLeave={
            isResolved ? () => setIsResolvedHovered(false) : undefined
          }
        >
          {isResolved ? (
            isResolvedHovered ? (
              <XCircle
                weight="regular"
                height="16"
                width="16"
                className="text-green-600"
              />
            ) : (
              <CheckCircle
                weight="fill"
                height="16"
                width="16"
                className="text-puntt-neutral-gray-11"
              />
            )
          ) : (
            <CheckCircle weight="regular" height="16" width="16" />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  const repliesCollapseCtx = useContext(RepliesCollapseContext);
  // Use threadId as meta._id for top-level comments
  const threadId = !isReply ? meta._id : undefined;
  // Default open unless explicitly collapsed
  const repliesCollapsed =
    threadId && repliesCollapseCtx?.collapsed[threadId] === true;
  const setRepliesCollapsed = (collapsed: boolean) => {
    if (threadId && repliesCollapseCtx) {
      repliesCollapseCtx.setCollapsed(threadId, collapsed);
    }
  };

  return (
    <>
      <div
        ref={itemRef}
        data-testid={isReply ? "comment-reply" : "comment"}
        className={cx("group/comment grid p-4 transition-colors", {
          "ml-4 border-l border-l-puntt-neutral-gray-6": isReply,
          "border-y border-y-puntt-neutral-gray-6": !isReply && !isCanvas,
          "border-t border-t-puntt-neutral-gray-6": isNotFirstReply,
          "border-b border-b-puntt-neutral-gray-6": isCanvas,
          "bg-puntt-accent-3": isActive,
          "hover:bg-puntt-accent-2":
            meta.x != null || meta.y != null || videoStart != null,
        })}
        tabIndex={-1}
        role="button"
        onClick={() => {
          // Only trigger onClick if the user hasn't selected any text
          if (window.getSelection()?.toString() === "") {
            onClick?.();
          }
        }}
        onKeyDown={(e) => {
          if (e.code === "space" && typeof onClick === "function") {
            onClick();
          }
        }}
        data-x={!isReply && meta.x}
        data-y={!isReply && meta.y}
        data-id={messageId}
        onMouseEnter={() => {
          // Only update highlighted comment if no text is selected
          if (window.getSelection()?.toString() === "") {
            dispatch(setHighlightedCommentId(commentId));
          }
        }}
        onMouseLeave={() => {
          // Only update highlighted comment if no text is selected
          if (window.getSelection()?.toString() === "") {
            dispatch(setHighlightedCommentId(null));
          }
        }}
      >
        {isAIPending ? (
          <section className="min-w-0">
            <Flex gap="2">
              {renderAvatar()}

              <article className="min-w-0 grow">
                <header className="relative mb-2 flex min-w-0 items-start justify-between">
                  <Text
                    size="3"
                    weight="medium"
                    className="min-w-0 hyphens-auto whitespace-normal break-words"
                  >
                    <Text>
                      {aiPersonaName ? (
                        <>
                          Puntt AI:{" "}
                          <Text as="span" className="capitalize">
                            {personaName(aiPersonaName)}
                          </Text>
                        </>
                      ) : (
                        "Puntt AI"
                      )}
                    </Text>
                  </Text>
                </header>

                <Button
                  className="pointer-events-none w-full bg-puntt-neutral-gray-8"
                  size="2"
                >
                  <img
                    alt="leap frog loader"
                    src={leapfrogLoader}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Button>
              </article>
            </Flex>
          </section>
        ) : (
          <>
            <section
              data-testid="comment-title"
              className={cx({ hidden: title == null })}
            >
              <Text size="2" weight="medium" className="text-carbon-300">
                {title}
              </Text>
            </section>
            <section className="min-w-0">
              <Flex
                gap="1"
                align="center"
                justify="end"
                className="ml-2 flex-1"
              >
                <Flex gap="2">
                  {renderThumbsUpAction()}
                  {renderThumbsDownAction()}
                </Flex>
                <Flex gap="1">
                  {renderEditAction()}
                  {renderDeleteAction()}
                  {renderDismissAction()}
                  {renderResolveOrUnresolveAction()}
                </Flex>
              </Flex>
              <Flex gap="2">
                {renderAvatar()}

                <article className="min-w-0 grow">
                  <header className="relative mb-2 flex min-w-0 items-start justify-between">
                    <Text
                      size="3"
                      weight="medium"
                      className="min-w-0 hyphens-auto whitespace-normal break-words"
                    >
                      {isAI ? <Text>Puntt AI</Text> : author?.name}
                    </Text>
                    <Text
                      size="1"
                      className={cx(
                        "pointer-events-none mt-1 max-w-[6rem] select-none hyphens-auto whitespace-normal break-words text-right text-puntt-neutral-grayA-9 transition-opacity group-hover/comment:opacity-0",
                        {
                          hidden:
                            meta.disposition !==
                              TicketCommentDisposition.DEFAULT || isAI,
                        },
                      )}
                    >
                      {formatDistanceToNowStrict(
                        new Date(isEdited ? updatedAt : createdAt),
                      )}{" "}
                      ago {isEdited && !isAI ? "(edited)" : null}
                    </Text>
                  </header>
                </article>
              </Flex>
            </section>
            <div className="mb-4">
              <Text
                className={cx("text-puntt-accent-12 underline", {
                  hidden: typeof videoStart !== "number",
                })}
                weight="medium"
                size="2"
              >
                {formatTimeStamp(videoStart)}
              </Text>
              {renderCommentContents()}
              {renderCitation()}
            </div>

            {/* TODO: the UX around replying to a resolved or rejected comment is strange and we need to rethink it. */}
            {meta.disposition === TicketCommentDisposition.DEFAULT && (
              <>
                <CommentMentions
                  key={`reply-input-${replies.length}`}
                  ref={mentionsRef}
                  data-testid="reply-input"
                  className={cx({
                    hidden: replies.length > 0 || isReply,
                  })}
                  placeholder="Reply or Ask @Puntt"
                  onChange={handleSetReplyText}
                  value={replyText}
                  disabled={isLoading || createCommentMutation.isPending}
                  loading={createCommentMutation.isPending}
                  onSend={handleReply}
                  data-auth-trigger="reply"
                />
                {showReplyInput && (
                  <CommentMentions
                    ref={mentionsRef}
                    data-testid="reply-input"
                    placeholder="Reply or Ask @Puntt"
                    onChange={handleSetReplyText}
                    value={replyText}
                    disabled={isLoading || createCommentMutation.isPending}
                    loading={createCommentMutation.isPending}
                    onSend={handleReply}
                    data-auth-trigger="reply"
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Hide/Show Replies bar */}
      {!isReply && replies.length > 0 && (
        <button
          className="sticky z-[1] flex w-full items-center justify-between border-b border-b-puntt-neutral-gray-6 px-4 py-2 text-left"
          style={{ backgroundColor: "#fff", outline: "none" }}
          onClick={() => setRepliesCollapsed(!repliesCollapsed)}
          data-testid="collapse-replies-toggle"
        >
          <Text
            size="1"
            weight="medium"
            className="max-w-full hyphens-auto whitespace-normal break-words"
          >
            {repliesCollapsed
              ? `Show Replies (${replies.length})`
              : `Hide Replies`}
          </Text>
          {repliesCollapsed ? <CaretLeft size={16} /> : <CaretDown size={16} />}
        </button>
      )}

      {/* Replies */}
      {!repliesCollapsed && (
        <div data-testid="comment-replies-container">
          {replies.map((reply, i) => (
            <Comment
              key={reply._id}
              author={
                reply.createdBy as Exclude<typeof reply.createdBy, string>
              }
              createdAt={reply.createdAt}
              isReply
              message={reply.description}
              messageId={reply._id}
              meta={meta}
              onClick={onClick}
              showReplyInput={i === replies.length - 1}
              isNotFirstReply={i !== 0}
              isLast={i === replies.length - 1}
              updatedAt={reply.updatedAt}
              onSuccess={onSuccess}
              onError={onError}
              editor={editor}
              users={users}
              mentions={reply.mentions as string[]}
              isActive={isActive}
              commentId={commentId}
              // @ts-expect-error TS2551: uhhhh.. Not sure what the key is.
              isAIPending={reply.aiPending}
              isAI={reply.isAI}
              isCanvas={isCanvas}
              citations={reply.citations}
              setMessage={setMessage}
              tempComment={replyText}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Export context provider for use in Comments.tsx or parent
export function RepliesCollapseProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [collapsed, setCollapsedState] = useState<Record<string, boolean>>({});

  // Always get the latest comments
  const comments = useAppSelector((state) => state.ticket.comments);
  const threadIds = comments
    .filter((c) => c.messages && c.messages.length > 0)
    .map((c) => c._id);

  // Set per-thread
  const setCollapsed = (threadId: string, value: boolean) => {
    setCollapsedState((prev) => ({ ...prev, [threadId]: value }));
  };

  // Set all threads
  const setAll = (value: boolean) => {
    setCollapsedState((_prev) => {
      const next: Record<string, boolean> = {};
      for (const id of threadIds) next[id] = value;
      return next;
    });
  };

  // Keep collapse state in sync with threadIds
  useEffect(() => {
    setCollapsedState((prev) => {
      const next = { ...prev };
      // Add new threads
      for (const id of threadIds) {
        if (!(id in next)) {
          next[id] = false; // Default to expanded
        }
      }
      // Remove threads that no longer exist
      Object.keys(next).forEach((id) => {
        if (!threadIds.includes(id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [threadIds.join(",")]);

  return (
    <RepliesCollapseContext.Provider
      value={{ collapsed, setCollapsed, setAll }}
    >
      {children}
    </RepliesCollapseContext.Provider>
  );
}
